import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { PlatformCommunicationEvents, SessionExpiredEventPayload } from '@shared/generalInterfaces';
import { Router } from '@angular/router';
import { Consts } from '@shared/consts';
import { ShellContextService } from '../shell-context/shell-context.service';
import { PlatformCommunicationService } from 'app/platform/services/platform-communication/platform-communication.service';
import { Location } from '@angular/common';
import { SessionInfo } from './auth.service';
import { AuthAngularService } from '@itero/auth-angular';
import { CookieReadWriteService } from './cookie-read-write.service';

@Injectable({
	providedIn: 'root',
})
export class TokenRefreshService {
	constructor(
		private router: Router,
		private shellContextService: ShellContextService,
		private platformCommunicationService: PlatformCommunicationService,
		private location: Location,
		private authAngularService: AuthAngularService,
		private cookieReadWriteService: CookieReadWriteService
	) {}

	handleAuthenticationRequired() {
		return this.authAngularService.loginRequired$.pipe(
			tap(authInfo => {
				this.dispatchExpiredTokenEvent(authInfo.sessionId);
			})
		);
	}

	handleRefreshSuccess() {
		return this.authAngularService.refreshSucceed$.pipe(
			tap(authInfo => {
				this.updateSessionInfo({ ...(authInfo as SessionInfo) });
			})
		);
	}

	dispatchExpiredTokenEvent(sessionId: string) {
		const payload: SessionExpiredEventPayload = { returnUrl: this.getReturnUrl() };

		const sessionExpired = new CustomEvent('midc_event_onTokenExpired', {
			detail: {
				appId: 'midc',
				sessionId,
				payload: payload.returnUrl?.includes('mfe-wrapper') ? {} : payload,
			},
		});

		window.dispatchEvent(sessionExpired);
	}

	private getReturnUrl() {
		const currentUrl = this.location.path();
		const params = this.router.parseUrl(currentUrl).queryParams;

		if (params.returnUrl) {
			return params.returnUrl;
		} else if (currentUrl) {
			return currentUrl;
		}
	}

	private updateSessionInfo(updatedSessionInfo: SessionInfo) {
		updatedSessionInfo.sessionType = Consts.SessionType;
		this.shellContextService.updateContext(context => {
			context.session.sessionId = updatedSessionInfo.sessionId;
			context.security.accessToken = updatedSessionInfo.accessToken;
		});

		const identityProvider = this.cookieReadWriteService.authCookie.cookie.identityProvider;

		this.cookieReadWriteService.clearAuthCookie();
		this.cookieReadWriteService.writeAuthCookie({
			accessToken: updatedSessionInfo.accessToken,
			sessionId: updatedSessionInfo.sessionId,
			idToken: updatedSessionInfo.idToken,
			identityProvider,
		});

		const tokenRefreshedEventPayload = { accessToken: updatedSessionInfo.accessToken };

		this.platformCommunicationService.publishEvent(PlatformCommunicationEvents.TokenRefreshed, ['*'], tokenRefreshedEventPayload);
	}
}
